import { createContext, useContext } from 'react';
import type { Language, Translations } from './types';
import { STAND_WORKSPACE_LOCALE_CONTRACTS } from './localeContracts';
import { logger } from '@/utils/debugLogger';
// Compile-time locale-contract enforcement: importing this module pulls the
// per-locale `satisfies` assertions into the type-check graph, so a locale
// missing a required key fails `npm run type-check`.
// The runtime loader below is unchanged; this import carries no runtime behaviour.

// Reference the contract export so the import is retained (and thus type-checked).
void STAND_WORKSPACE_LOCALE_CONTRACTS;

// Static module map produced by Vite at transform time. Use eager imports for
// root-shell locale files so the first render does not fan out runtime requests
// for tiny JSON chunks under parallel Playwright shards. Because the glob is
// eager, every locale bundle is statically compiled into the app — there is no
// runtime fetch that can fail, which is what lets the emergency fallback below
// be assembled synchronously from the real English translations.
const localeModules = import.meta.glob<{ default: unknown }>(
  './locales/*/{common,browseProperties,detailedAnalysis,priceEstimate,landing,planner,standWorkspace,auth,terminology,timberPrices,about,shell,settings,implementView,measurementUpload,offerDecode}.json',
  { eager: true },
);

type LocaleModule = { default: unknown };

const loadModule = async (lang: Language, file: string): Promise<LocaleModule | null> => {
  const key = `./locales/${lang}/${file}.json`;
  return localeModules[key] ?? null;
};

// Synchronous accessor for the eagerly-bundled modules — used to assemble the
// emergency fallback at module-init time (no async, no fetch).
const syncModule = (lang: Language, file: string): LocaleModule | null =>
  localeModules[`./locales/${lang}/${file}.json`] ?? null;

// Slice extractor keeps assembleTranslations under the complexity gate: the
// optional-chain / cast lives here (one small function) instead of being inlined
// once per locale file in the assembly object. The `as` asserts each slice to its
// declared type; a missing optional module yields `undefined` at runtime (its
// key is optional in Translations).
const pick = <K extends keyof Translations>(
  mod: LocaleModule | null | undefined,
): Translations[K] => mod?.default as Translations[K];

// Assemble a full Translations object from the 16-element module array. Index
// order is shared by loadTranslations (async) and the emergency fallback (sync)
// so both produce structurally identical, type-correct Translations.
const assembleTranslations = (modules: (LocaleModule | null)[]): Translations => ({
  common: pick<'common'>(modules[0]),
  browseProperties: pick<'browseProperties'>(modules[1]),
  detailedAnalysis: pick<'detailedAnalysis'>(modules[2]),
  priceEstimate: pick<'priceEstimate'>(modules[3]),
  landing: pick<'landing'>(modules[4]),
  planner: pick<'planner'>(modules[5]),
  standWorkspace: pick<'standWorkspace'>(modules[6]),
  auth: pick<'auth'>(modules[7]),
  terminology: pick<'terminology'>(modules[8]),
  timberPrices: pick<'timberPrices'>(modules[9]),
  about: pick<'about'>(modules[10]),
  shell: pick<'shell'>(modules[11]),
  settings: pick<'settings'>(modules[12]),
  implementView: pick<'implementView'>(modules[13]),
  measurementUpload: pick<'measurementUpload'>(modules[14]),
  offerDecode: pick<'offerDecode'>(modules[15]),
});

// Ordered list of locale files that map to the assembleTranslations indices.
const LOCALE_FILES = [
  'common',
  'browseProperties',
  'detailedAnalysis',
  'priceEstimate',
  'landing',
  'planner',
  'standWorkspace',
  'auth',
  'terminology',
  'timberPrices',
  'about',
  'shell',
  'settings',
  'implementView',
  'measurementUpload',
  'offerDecode',
] as const;

// Used when ALL runtime locale loading fails. Exported for unit testing.
//
// This is NOT a hand-maintained partial stub: it is assembled from the REAL,
// eagerly-bundled English locale JSON, so every required slice/key is present
// and type-correct with no double-cast lie. Because the locale glob is eager,
// the English modules are always compiled into the bundle — assembling them
// here cannot trigger a runtime fetch, making this a safe last-resort value
// that satisfies the full `Translations` contract.
export const EMERGENCY_FALLBACK_TRANSLATIONS: Translations = assembleTranslations(
  LOCALE_FILES.map((file) => syncModule('en', file)),
);

export interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
  isLoading: boolean;
}

export const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const loadTranslations = async (lang: Language): Promise<Translations> => {
  try {
    const modules = await Promise.all([
      loadModule(lang, 'common'),
      loadModule(lang, 'browseProperties'),
      loadModule(lang, 'detailedAnalysis'),
      loadModule(lang, 'priceEstimate'),
      loadModule(lang, 'landing').catch(() => null),
      loadModule(lang, 'planner').catch(() => null),
      loadModule(lang, 'standWorkspace').catch(() => null),
      loadModule(lang, 'auth').catch(() => null),
      loadModule(lang, 'terminology'),
      loadModule(lang, 'timberPrices').catch(() => null),
      loadModule(lang, 'about').catch(() => null),
      loadModule(lang, 'shell'),
      loadModule(lang, 'settings').catch(() => null),
      loadModule(lang, 'implementView').catch(() => null),
      loadModule(lang, 'measurementUpload').catch(() => null),
      loadModule(lang, 'offerDecode').catch(() => null),
    ]);

    if (!modules[0] || !modules[1] || !modules[2] || !modules[3] || !modules[8] || !modules[11]) {
      throw new Error(`Required translation files missing for ${lang}`);
    }

    return assembleTranslations(modules);
  } catch (error) {
    logger.error(
      { component: 'LanguageContext', action: 'loadTranslations' },
      `Failed to load translations for ${lang}`,
      error,
    );
    // Fallback to English if loading fails
    if (lang !== 'en') {
      return loadTranslations('en');
    }
    throw error;
  }
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
